import { registerTldrawLibraryVersion } from "@tldraw/utils";
import { assetIdValidator, createAssetValidator } from "./assets/TLBaseAsset.mjs";
import {
  bookmarkAssetMigrations,
  bookmarkAssetProps
} from "./assets/TLBookmarkAsset.mjs";
import { imageAssetMigrations, imageAssetProps } from "./assets/TLImageAsset.mjs";
import { videoAssetMigrations, videoAssetProps } from "./assets/TLVideoAsset.mjs";
import {
  arrowBindingMigrations,
  arrowBindingProps,
  arrowBindingVersions,
  ElbowArrowSnap
} from "./bindings/TLArrowBinding.mjs";
import {
  bindingIdValidator,
  createBindingValidator
} from "./bindings/TLBaseBinding.mjs";
import {
  createPresenceStateDerivation,
  getDefaultUserPresence
} from "./createPresenceStateDerivation.mjs";
import {
  createTLSchema,
  defaultAssetSchemas,
  defaultBindingSchemas,
  defaultShapeSchemas
} from "./createTLSchema.mjs";
import {
  boxModelValidator,
  vecModelValidator
} from "./misc/geometry-types.mjs";
import { idValidator } from "./misc/id-validator.mjs";
import {
  canvasUiColorTypeValidator,
  TL_CANVAS_UI_COLOR_TYPES
} from "./misc/TLColor.mjs";
import { TL_CURSOR_TYPES } from "./misc/TLCursor.mjs";
import { TL_HANDLE_TYPES } from "./misc/TLHandle.mjs";
import { opacityValidator } from "./misc/TLOpacity.mjs";
import { richTextValidator, toRichText } from "./misc/TLRichText.mjs";
import { scribbleValidator, TL_SCRIBBLE_STATES } from "./misc/TLScribble.mjs";
import {
  assetMigrations,
  AssetRecordType,
  createAssetPropsMigrationIds,
  createAssetPropsMigrationSequence,
  createAssetRecordType
} from "./records/TLAsset.mjs";
import {
  createBindingId,
  createBindingPropsMigrationIds,
  createBindingPropsMigrationSequence,
  isBinding,
  isBindingId,
  rootBindingMigrations
} from "./records/TLBinding.mjs";
import { CameraRecordType } from "./records/TLCamera.mjs";
import {
  commentReactionRecordConfig,
  commentRecordConfig,
  commentSchemaRecords,
  commentThreadRecordConfig,
  createComment,
  createCommentId,
  createCommentReaction,
  createCommentReactionId,
  createCommentThread,
  createCommentThreadId,
  isCommentId,
  isCommentReactionId,
  isCommentThreadId
} from "./records/TLComment.mjs";
import {
  createCustomRecordId,
  createCustomRecordMigrationIds,
  createCustomRecordMigrationSequence,
  isCustomRecord,
  isCustomRecordId
} from "./records/TLCustomRecord.mjs";
import {
  DocumentRecordType,
  isDocument,
  TLDOCUMENT_ID
} from "./records/TLDocument.mjs";
import {
  pluckPreservingValues,
  TLINSTANCE_ID
} from "./records/TLInstance.mjs";
import {
  isPageId,
  pageIdValidator,
  PageRecordType
} from "./records/TLPage.mjs";
import {
  InstancePageStateRecordType
} from "./records/TLPageState.mjs";
import {
  PointerRecordType,
  TLPOINTER_ID
} from "./records/TLPointer.mjs";
import {
  InstancePresenceRecordType
} from "./records/TLPresence.mjs";
import {
  createShapeId,
  createShapePropsMigrationIds,
  createShapePropsMigrationSequence,
  getShapePropKeysByStyle,
  isShape,
  isShapeId,
  rootShapeMigrations
} from "./records/TLShape.mjs";
import {
  createUserId,
  createUserRecordType,
  isUserId,
  userIdValidator,
  UserRecordType
} from "./records/TLUser.mjs";
import {
  ArrowShapeArrowheadEndStyle,
  ArrowShapeArrowheadStartStyle,
  ArrowShapeKindStyle,
  arrowShapeMigrations,
  arrowShapeProps,
  arrowShapeVersions
} from "./shapes/TLArrowShape.mjs";
import {
  createShapeValidator,
  parentIdValidator,
  shapeIdValidator
} from "./shapes/TLBaseShape.mjs";
import {
  bookmarkShapeMigrations,
  bookmarkShapeProps
} from "./shapes/TLBookmarkShape.mjs";
import {
  compressLegacySegments,
  drawShapeMigrations,
  drawShapeProps
} from "./shapes/TLDrawShape.mjs";
import {
  embedShapeMigrations,
  embedShapeProps
} from "./shapes/TLEmbedShape.mjs";
import {
  frameShapeMigrations,
  frameShapeProps
} from "./shapes/TLFrameShape.mjs";
import {
  GeoShapeGeoStyle,
  geoShapeMigrations,
  geoShapeProps
} from "./shapes/TLGeoShape.mjs";
import {
  groupShapeMigrations,
  groupShapeProps
} from "./shapes/TLGroupShape.mjs";
import {
  highlightShapeMigrations,
  highlightShapeProps
} from "./shapes/TLHighlightShape.mjs";
import {
  ImageShapeCrop,
  imageShapeMigrations,
  imageShapeProps
} from "./shapes/TLImageShape.mjs";
import {
  lineShapeMigrations,
  lineShapeProps,
  LineShapeSplineStyle
} from "./shapes/TLLineShape.mjs";
import {
  noteShapeMigrations,
  noteShapeProps
} from "./shapes/TLNoteShape.mjs";
import {
  textShapeMigrations,
  textShapeProps
} from "./shapes/TLTextShape.mjs";
import {
  videoShapeMigrations,
  videoShapeProps
} from "./shapes/TLVideoShape.mjs";
import { EnumStyleProp, StyleProp } from "./styles/StyleProp.mjs";
import {
  DefaultColorStyle,
  registerColorsFromThemes
} from "./styles/TLColorStyle.mjs";
import { DefaultDashStyle } from "./styles/TLDashStyle.mjs";
import { DefaultFillStyle } from "./styles/TLFillStyle.mjs";
import {
  DefaultFontFamilies,
  DefaultFontStyle,
  isFontEntry,
  registerFontsFromThemes
} from "./styles/TLFontStyle.mjs";
import {
  DefaultHorizontalAlignStyle
} from "./styles/TLHorizontalAlignStyle.mjs";
import { DefaultSizeStyle } from "./styles/TLSizeStyle.mjs";
import { DefaultTextAlignStyle } from "./styles/TLTextAlignStyle.mjs";
import {
  DefaultVerticalAlignStyle
} from "./styles/TLVerticalAlignStyle.mjs";
import {
  createCachedUserResolve
} from "./TLStore.mjs";
import {
  getDefaultTranslationLocale,
  LANGUAGES
} from "./translations/translations.mjs";
registerTldrawLibraryVersion(
  "@tldraw/tlschema",
  "5.3.0",
  "esm"
);
import { DIM_2D, DIM_3D, b64Vecs } from "./misc/b64Vecs.mjs";
export {
  ArrowShapeArrowheadEndStyle,
  ArrowShapeArrowheadStartStyle,
  ArrowShapeKindStyle,
  AssetRecordType,
  CameraRecordType,
  DIM_2D,
  DIM_3D,
  DefaultColorStyle,
  DefaultDashStyle,
  DefaultFillStyle,
  DefaultFontFamilies,
  DefaultFontStyle,
  DefaultHorizontalAlignStyle,
  DefaultSizeStyle,
  DefaultTextAlignStyle,
  DefaultVerticalAlignStyle,
  DocumentRecordType,
  ElbowArrowSnap,
  EnumStyleProp,
  GeoShapeGeoStyle,
  ImageShapeCrop,
  InstancePageStateRecordType,
  InstancePresenceRecordType,
  LANGUAGES,
  LineShapeSplineStyle,
  PageRecordType,
  PointerRecordType,
  StyleProp,
  TLDOCUMENT_ID,
  TLINSTANCE_ID,
  TLPOINTER_ID,
  TL_CANVAS_UI_COLOR_TYPES,
  TL_CURSOR_TYPES,
  TL_HANDLE_TYPES,
  TL_SCRIBBLE_STATES,
  UserRecordType,
  arrowBindingMigrations,
  arrowBindingProps,
  arrowBindingVersions,
  arrowShapeMigrations,
  arrowShapeProps,
  arrowShapeVersions,
  assetIdValidator,
  assetMigrations,
  b64Vecs,
  bindingIdValidator,
  bookmarkAssetMigrations,
  bookmarkAssetProps,
  bookmarkShapeMigrations,
  bookmarkShapeProps,
  boxModelValidator,
  canvasUiColorTypeValidator,
  commentReactionRecordConfig,
  commentRecordConfig,
  commentSchemaRecords,
  commentThreadRecordConfig,
  compressLegacySegments,
  createAssetPropsMigrationIds,
  createAssetPropsMigrationSequence,
  createAssetRecordType,
  createAssetValidator,
  createBindingId,
  createBindingPropsMigrationIds,
  createBindingPropsMigrationSequence,
  createBindingValidator,
  createCachedUserResolve,
  createComment,
  createCommentId,
  createCommentReaction,
  createCommentReactionId,
  createCommentThread,
  createCommentThreadId,
  createCustomRecordId,
  createCustomRecordMigrationIds,
  createCustomRecordMigrationSequence,
  createPresenceStateDerivation,
  createShapeId,
  createShapePropsMigrationIds,
  createShapePropsMigrationSequence,
  createShapeValidator,
  createTLSchema,
  createUserId,
  createUserRecordType,
  defaultAssetSchemas,
  defaultBindingSchemas,
  defaultShapeSchemas,
  drawShapeMigrations,
  drawShapeProps,
  embedShapeMigrations,
  embedShapeProps,
  frameShapeMigrations,
  frameShapeProps,
  geoShapeMigrations,
  geoShapeProps,
  getDefaultTranslationLocale,
  getDefaultUserPresence,
  getShapePropKeysByStyle,
  groupShapeMigrations,
  groupShapeProps,
  highlightShapeMigrations,
  highlightShapeProps,
  idValidator,
  imageAssetMigrations,
  imageAssetProps,
  imageShapeMigrations,
  imageShapeProps,
  isBinding,
  isBindingId,
  isCommentId,
  isCommentReactionId,
  isCommentThreadId,
  isCustomRecord,
  isCustomRecordId,
  isDocument,
  isFontEntry,
  isPageId,
  isShape,
  isShapeId,
  isUserId,
  lineShapeMigrations,
  lineShapeProps,
  noteShapeMigrations,
  noteShapeProps,
  opacityValidator,
  pageIdValidator,
  parentIdValidator,
  pluckPreservingValues,
  registerColorsFromThemes,
  registerFontsFromThemes,
  richTextValidator,
  rootBindingMigrations,
  rootShapeMigrations,
  scribbleValidator,
  shapeIdValidator,
  textShapeMigrations,
  textShapeProps,
  toRichText,
  userIdValidator,
  vecModelValidator,
  videoAssetMigrations,
  videoAssetProps,
  videoShapeMigrations,
  videoShapeProps
};
//# sourceMappingURL=index.mjs.map
